/**
 * The form styling for the plan and tier pages.
 *
 * Shared on purpose: a tier is authored the same way a plan is — the same
 * sections, the same feature cards — and two copies of this would drift apart
 * the first time one of them was touched.
 *
 * Injected by the page as a <style> tag, scoped under `.asp-scope`.
 */
export const PLAN_FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  .asp-scope {
    font-family: 'Plus Jakarta Sans', sans-serif;
  }

  /* ── Page title ── */
  .asp-scope .page-title-box {
    padding: 6px 0 22px;
    border-bottom: 1px solid #f0f2f7;
    margin-bottom: 26px;
  }
  .asp-scope .page-title-box h4 {
    font-size: 20px;
    font-weight: 800;
    color: #1a1f36;
    letter-spacing: -0.3px;
    margin: 0;
  }
  .asp-scope .breadcrumb { background: none; padding: 0; margin: 0; }
  .asp-scope .breadcrumb-item a { color: #8b92a9; text-decoration: none; font-size: 13px; font-weight: 500; }
  .asp-scope .breadcrumb-item.active { color: #3b5bdb; font-size: 13px; font-weight: 600; }
  .asp-scope .breadcrumb-item + .breadcrumb-item::before { color: #c8cdd9; }

  /* ── Card ── */
  .asp-scope .card {
    border: 1px solid #eaecf4 !important;
    border-radius: 16px !important;
    box-shadow: 0 2px 20px rgba(28,36,75,0.07) !important;
    overflow: visible !important;
  }
  .asp-scope .card-body { padding: 0 !important; }

  /* ── Card Header ── */
  .asp-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 22px 28px 20px;
    border-bottom: 1px solid #f0f2f7;
    background: #fafbff;
    border-radius: 16px 16px 0 0;
  }
  .asp-card-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #eef1fb, #dde3f8);
    border: 1.5px solid #d0d8f5;
    display: flex; align-items: center; justify-content: center;
    color: #3b5bdb;
    font-size: 16px;
    flex-shrink: 0;
  }
  .asp-card-title {
    font-size: 15px;
    font-weight: 800;
    color: #1a1f36;
    letter-spacing: -0.2px;
  }
  .asp-card-subtitle {
    font-size: 12.5px;
    color: #8b92a9;
    font-weight: 500;
    margin-top: 1px;
  }
  .asp-mode-badge {
    margin-left: auto;
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .asp-mode-badge-edit   { background: #fff8ec; color: #e07b00; border: 1px solid #fde8b8; }
  .asp-mode-badge-create { background: #edfaf3; color: #0a8a4a; border: 1px solid #b8f0d4; }

  /* ── Form body ── */
  .asp-form-body { padding: 28px 28px 24px; }

  /* ── Section divider ── */
  .asp-section {
    margin-bottom: 32px;
  }
  .asp-section-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    color: #8b92a9;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 18px;
  }
  .asp-section-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #f0f2f7;
  }

  /* ── Form Group override ── */
  .asp-scope .form-group.row { margin-bottom: 0 !important; }
  .asp-field { margin-bottom: 20px; }
  .asp-label {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #1a1f36;
    margin-bottom: 7px;
    letter-spacing: -0.1px;
  }
  .asp-label span.asp-req { color: #d92b2b; margin-left: 2px; }

  /* ── Inputs ── */
  .asp-scope .form-control {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1a1f36;
    background: #fff;
    border: 1.5px solid #e2e6f0;
    border-radius: 10px;
    padding: 10px 14px;
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
    box-shadow: 0 1px 3px rgba(28,36,75,0.04);
  }
  .asp-scope .form-control:focus {
    border-color: #3b5bdb;
    box-shadow: 0 0 0 3px rgba(59,91,219,0.12);
    outline: none;
  }
  .asp-scope .form-control.is-invalid {
    border-color: #d92b2b;
    box-shadow: 0 0 0 3px rgba(217,43,43,0.10);
  }
  .asp-scope .form-control::placeholder { color: #b0b8cc; font-weight: 400; }
  .asp-scope textarea.form-control { resize: vertical; min-height: 90px; line-height: 1.55; }

  /* ── Error text ── */
  .asp-error {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 600;
    color: #d92b2b;
    margin-top: 5px;
  }
  .asp-error::before { content: '⚠'; font-size: 11px; }

  /* ── Hint text ── */
  .asp-hint {
    font-size: 12px;
    color: #8b92a9;
    font-weight: 500;
    margin-top: 5px;
    line-height: 1.5;
  }

  /* ── Toggle / Checkbox Row ── */
  .asp-toggle-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: #f8f9fe;
    border: 1.5px solid #e2e6f0;
    border-radius: 10px;
    padding: 14px 16px;
    transition: border-color 0.18s ease;
  }
  .asp-toggle-row:focus-within { border-color: #3b5bdb; }
  .asp-toggle-row .form-check-input {
    width: 20px !important;
    height: 20px !important;
    margin-top: 2px;
    flex-shrink: 0;
    border: 2px solid #c8d0e8;
    border-radius: 5px;
    cursor: pointer;
    accent-color: #3b5bdb;
  }
  .asp-toggle-row .form-check-input:checked { background-color: #3b5bdb; border-color: #3b5bdb; }
  .asp-toggle-info strong.ok  { color: #0a8a4a; }
  .asp-toggle-info strong.no  { color: #d92b2b; }
  .asp-toggle-info strong     { font-weight: 700; }
  .asp-toggle-info small      { font-size: 13px; color: #5a6180; line-height: 1.5; }

  /* ── Features Grid ── */
  .asp-feature-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
  }
  .asp-feature-card {
    background: #fff;
    border: 1.5px solid #e2e6f0;
    border-radius: 12px;
    padding: 14px;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
    cursor: pointer;
    position: relative;
    min-height: 0;
    overflow: hidden;
    word-break: break-word;
  }
  .asp-feature-card:hover { border-color: #b0bce8; box-shadow: 0 2px 10px rgba(59,91,219,0.08); }
  .asp-feature-card.asp-feature-checked {
    border-color: #3b5bdb;
    background: #f4f6fd;
    box-shadow: 0 2px 12px rgba(59,91,219,0.12);
  }
  .asp-feature-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
    min-width: 0;
  }
  .asp-feature-check-label {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    cursor: pointer;
    flex: 1;
    min-width: 0;
  }
  .asp-feature-check-label input[type="checkbox"] {
    width: 16px !important;
    height: 16px !important;
    margin-top: 2px;
    flex-shrink: 0;
    accent-color: #3b5bdb;
    cursor: pointer;
    border-radius: 4px;
  }
  .asp-feature-name {
    font-size: 13.5px;
    font-weight: 700;
    color: #1a1f36;
    line-height: 1.35;
    word-break: break-word;
    overflow-wrap: anywhere;
    white-space: normal;
  }
  .asp-feature-desc {
    font-size: 12px;
    color: #8b92a9;
    font-weight: 400;
    margin-top: 4px;
    line-height: 1.45;
  }
  .asp-feature-edit-btn {
    width: 28px; height: 28px;
    border-radius: 7px;
    border: 1.5px solid #e2e6f0;
    background: #fff;
    color: #8b92a9;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }
  .asp-feature-edit-btn:hover {
    border-color: #3b5bdb;
    background: #eef1fb;
    color: #3b5bdb;
  }
  .asp-feature-selected-tick {
    position: absolute;
    top: 10px; right: 10px;
    width: 18px; height: 18px;
    background: #3b5bdb;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: #fff;
    font-size: 10px;
    opacity: 0;
    transform: scale(0.6);
    transition: all 0.18s ease;
  }
  .asp-feature-checked .asp-feature-selected-tick { opacity: 1; transform: scale(1); }

  /* ── Feature loading skeleton ── */
  .asp-skeleton {
    background: linear-gradient(90deg, #f0f2f7 25%, #e8ebf5 50%, #f0f2f7 75%);
    background-size: 200% 100%;
    animation: asp-shimmer 1.4s infinite;
    border-radius: 8px;
    display: block;
  }
  @keyframes asp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

  /* ── Form footer ── */
  .asp-form-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 18px 28px 24px;
    border-top: 1px solid #f0f2f7;
    background: #fafbff;
    border-radius: 0 0 16px 16px;
  }
  .asp-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    padding: 10px 24px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    text-decoration: none !important;
    transition: all 0.18s ease;
    letter-spacing: -0.1px;
  }
  .asp-btn:disabled { opacity: 0.65; cursor: not-allowed; }
  .asp-btn-cancel {
    background: #f0f2f7;
    color: #5a6180;
    border: 1.5px solid #e2e6f0;
  }
  .asp-btn-cancel:hover { background: #e4e8f0; color: #1a1f36; }
  .asp-btn-submit {
    background: linear-gradient(135deg, #3b5bdb 0%, #4c6ef5 100%);
    color: #fff;
    box-shadow: 0 4px 14px rgba(59,91,219,0.35);
  }
  .asp-btn-submit:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(59,91,219,0.45);
  }

  /* ── Loading state ── */
  .asp-page-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    gap: 14px;
  }
  .asp-page-loader .spinner-border { width: 36px; height: 36px; border-width: 3px; color: #3b5bdb; }
  .asp-page-loader p { font-size: 14px; font-weight: 600; color: #8b92a9; margin: 0; }

  /* ── Spinner in button ── */
  .asp-scope .spinner-border-sm { width: 14px; height: 14px; border-width: 2px; }

  /* ── Two-col grid for price fields ── */
  .asp-price-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  @media (max-width: 640px) {
    .asp-price-grid { grid-template-columns: 1fr; }
    .asp-feature-grid { grid-template-columns: 1fr; }
  }
`;
